import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { supabase } from "../config";
import {
  CATEGORY_LABELS,
  DB_TO_URL_CATEGORY,
  CATEGORIES_FOR_YEAR,
  SEASON_EDITION_LABEL,
} from "../utils/jeedigunta";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";

const JeedigntaSeason = () => {
  const { seasonSlug } = useParams();
  const [season, setSeason] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    document.title = `Jeedigunta Kathakala Puraskaralu ${seasonSlug || ""}`.trim();
  }, [seasonSlug]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: s } = await supabase
        .from("jeedigunta_seasons")
        .select("season_id, season_name, season_year, slug, status, date_from, date_to")
        .eq("slug", seasonSlug)
        .maybeSingle();
      if (!s) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setSeason(s);
      const { data: r } = await supabase
        .from("jeedigunta_results")
        .select("category, rank, is_special_jury")
        .eq("season_id", s.season_id);
      setResults(r || []);
      setLoading(false);
    })();
  }, [seasonSlug]);

  const categoriesPresent = useMemo(() => {
    const set = new Set((results || []).map((r) => r.category));
    // Preferred display order from CATEGORIES_FOR_YEAR; fallback to insertion order.
    const preferred = CATEGORIES_FOR_YEAR[seasonSlug] || [];
    const ordered = preferred.filter((c) => set.has(c));
    set.forEach((c) => {
      if (!ordered.includes(c)) ordered.push(c);
    });
    return ordered;
  }, [results, seasonSlug]);

  const countsByCategory = useMemo(() => {
    const m = {};
    (results || []).forEach((r) => {
      m[r.category] = m[r.category] || { podium: 0, special: 0 };
      if (r.is_special_jury) m[r.category].special += 1;
      else m[r.category].podium += 1;
    });
    return m;
  }, [results]);

  if (notFound) return <Navigate to="/jeedigunta" replace />;

  const ed = season ? SEASON_EDITION_LABEL[season.slug] || { te: "", en: "" } : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-kk-ink via-kk-maroon-deep to-kk-ink text-kk-cream">
      <div className="max-w-6xl mx-auto px-4 pt-4 pb-10 md:pt-6 md:pb-12">
        <Link
          to="/jeedigunta"
          className="inline-flex items-center gap-2 text-kk-gold/70 hover:text-kk-gold mb-4 text-sm"
        >
          <FaArrowLeft /> All seasons
        </Link>

        <div className="grid md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start">
          {/* ── LEFT: Hero logo (sticky on desktop) ── */}
          <div className="flex justify-center md:justify-start md:sticky md:top-6">
            <img
              src="/logos/jeedigunta_puraskaralu_logo.jpg"
              alt="జీడిగుంట కథాకళ పురస్కారాలు"
              className="w-40 md:w-52 lg:w-60 h-auto rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.5)] border border-kk-gold/30"
            />
          </div>

          {/* ── RIGHT: title + categories ── */}
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-kk-gold-light mb-1">
              జీడిగుంట కథాకళ పురస్కారాలు {season?.slug || ""}
            </h1>
            {ed && (
              <p className="text-base md:text-lg text-kk-cream/80 italic">
                {ed.te} · <span className="font-serif">{ed.en}</span>
              </p>
            )}

            <h2 className="mt-6 md:mt-8 text-lg md:text-xl font-semibold text-kk-gold mb-4">
              విభాగం ఎంచుకోండి · Browse by Category
            </h2>

            {loading ? (
              <p className="text-kk-cream/60">Loading…</p>
            ) : categoriesPresent.length === 0 ? (
              <p className="text-kk-cream/60">No winners published yet for this cycle.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {categoriesPresent.map((cat) => {
                  const lbl = CATEGORY_LABELS[cat] || { te: cat, en: cat };
                  const counts = countsByCategory[cat] || { podium: 0, special: 0 };
                  const urlCat = DB_TO_URL_CATEGORY[cat] || cat.replace(/_/g, "-");
                  return (
                    <Link
                      key={cat}
                      to={`/jeedigunta/${seasonSlug}/${urlCat}`}
                      className="group block p-5 bg-kk-ink/60 border border-kk-gold/20 rounded-xl hover:border-kk-gold/60 hover:shadow-[0_0_20px_rgba(212,168,67,0.18)] transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xl md:text-2xl font-bold text-kk-cream">
                          {lbl.te}
                        </span>
                        <FaArrowRight className="text-kk-gold/60 group-hover:text-kk-gold group-hover:translate-x-1 transition-all" />
                      </div>
                      <div className="text-sm text-kk-cream/70 italic font-serif mb-3">
                        {lbl.en}
                      </div>
                      <div className="text-xs text-kk-gold/70">
                        {counts.podium} podium
                        {counts.special > 0 && ` · ${counts.special} special jury`}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JeedigntaSeason;
